"use strict";
/**
 * Event Type Service
 * Manages event type classification (baseball, basketball, football, etc.)
 * and persists baseball game state (inning, half-inning, offense) in
 * localStorage. Score tracking was removed with the scoreboard; older stored
 * entries may still carry scoreA/scoreB, which are simply ignored.
 * Entries are keyed by date so nightly cleanup removes stale data.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EVENT_TYPE_EMOJI = void 0;
exports.nextHalfInning = nextHalfInning;
exports.previousHalfInning = previousHalfInning;
exports.getEventType = getEventType;
exports.saveEventType = saveEventType;
exports.updateBaseballState = updateBaseballState;
exports.cleanupStaleEventTypes = cleanupStaleEventTypes;
/** The half-inning after this one: top N → bottom N → top N+1. */
function nextHalfInning(pos) {
    return pos.halfInning === "top"
        ? { inning: pos.inning, halfInning: "bottom" }
        : { inning: pos.inning + 1, halfInning: "top" };
}
/**
 * The half-inning before this one — the exact inverse of `nextHalfInning`, so
 * the step-back control undoes an accidental advance. `null` at the top of the
 * 1st: there is no earlier half, and the UI disables the control there. This
 * pair is the single source of truth for the clock, so the floor can't drift
 * between the handler and the button's enabled state.
 */
function previousHalfInning(pos) {
    if (pos.halfInning === "bottom") {
        return { inning: pos.inning, halfInning: "top" };
    }
    if (pos.inning <= 1)
        return null;
    return { inning: pos.inning - 1, halfInning: "bottom" };
}
/** Map event types to their display emoji */
exports.EVENT_TYPE_EMOJI = {
    baseball: "⚾",
    basketball: "🏀",
    football: "🏈",
    hockey: "🏒",
    soccer: "⚽",
    other: "📋",
};
const EVENT_TYPES_STORAGE_KEY = "kalshi_event_types";
function todayKey() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
/**
 * Load all event type entries from localStorage.
 */
function loadAll() {
    try {
        const raw = localStorage.getItem(EVENT_TYPES_STORAGE_KEY);
        if (raw) {
            return JSON.parse(raw);
        }
    }
    catch (e) {
        console.error("Failed to load event types:", e);
    }
    return {};
}
/**
 * Save all event type entries to localStorage.
 */
function saveAll(data) {
    try {
        localStorage.setItem(EVENT_TYPES_STORAGE_KEY, JSON.stringify(data));
    }
    catch (e) {
        console.error("Failed to save event types:", e);
    }
}
/**
 * Get the event type entry for a given event ID, if it exists and is from today.
 */
function getEventType(eventId) {
    const all = loadAll();
    const entry = all[eventId];
    if (entry && entry.date === todayKey()) {
        return entry;
    }
    return null;
}
/**
 * Save an event type. For baseball, initializes inning/halfInning/offense state.
 */
function saveEventType(eventId, type) {
    const all = loadAll();
    if (type === "baseball") {
        all[eventId] = {
            type: "baseball",
            inning: 1,
            halfInning: "top",
            offenseTeam: null,
            date: todayKey(),
        };
    }
    else {
        all[eventId] = {
            type,
            date: todayKey(),
        };
    }
    saveAll(all);
}
/**
 * Update baseball game state (inning/halfInning/offenseTeam) for an event.
 */
function updateBaseballState(eventId, updates) {
    const all = loadAll();
    const entry = all[eventId];
    if (entry && entry.type === "baseball") {
        if (updates.inning !== undefined)
            entry.inning = Math.max(1, updates.inning);
        if (updates.halfInning !== undefined)
            entry.halfInning = updates.halfInning;
        if (updates.offenseTeam !== undefined)
            entry.offenseTeam = updates.offenseTeam;
        saveAll(all);
    }
}
/**
 * Remove stale entries (anything not from today).
 * Should be called on app initialization.
 */
function cleanupStaleEventTypes() {
    const all = loadAll();
    const today = todayKey();
    let changed = false;
    for (const key of Object.keys(all)) {
        if (all[key].date !== today) {
            delete all[key];
            changed = true;
        }
    }
    if (changed) {
        saveAll(all);
    }
}
